import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, UserCheck, FileClock, ClipboardList, Trophy, Activity, Inbox, Briefcase, Receipt, BarChart3, Radio, UserPlus } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { api } from "@/lib/apiClient";
import { Loader, PageHeader } from "@/components/common";

const CARDS = [
  { key: "total_students", label: "Total Students", icon: Users, color: "text-teal", to: "/admin/students" },
  { key: "active_students", label: "Active Students", icon: UserCheck, color: "text-brandgreen", to: "/admin/students?status=active" },
  { key: "pending_reviews", label: "Pending Reviews", icon: FileClock, color: "text-warning", to: "/admin/reviews" },
  { key: "assessment_submissions", label: "Assessment Submissions", icon: ClipboardList, color: "text-teal", to: "/admin/students" },
  { key: "published_wins", label: "Published Wins", icon: Trophy, color: "text-brandgreen", to: "/admin/content" },
  { key: "lesson_activity", label: "Lesson Completions", icon: Activity, color: "text-teal", to: "/admin/students" },
  { key: "open_inquiries", label: "Open Inquiries", icon: Inbox, color: "text-warning", to: "/admin/messages" },
  { key: "active_jobs", label: "Active Job Listings", icon: Briefcase, color: "text-teal", to: "/admin/jobs" },
  { key: "pending_proofs", label: "Pending Payment Proofs", icon: Receipt, color: "text-warning", to: "/admin/products" },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsErr, setAnalyticsErr] = useState(false);
  const [live, setLive] = useState(null);
  const [refreshed, setRefreshed] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/analytics?days=14").then((r) => setAnalytics(r.data)).catch(() => setAnalyticsErr(true));
  }, []);
  useEffect(() => {
    const loadLive = () => api.get("/admin/live-stats").then((r) => { setLive(r.data); setRefreshed(new Date()); }).catch(() => {});
    loadLive();
    const id = setInterval(loadLive, 300000);
    return () => clearInterval(id);
  }, []);
  if (!stats) return <Loader />;

  const chartData = (analytics?.series || []).map((s) => ({
    day: s.date.slice(5), Courses: s.course_hours, Other: s.other_hours,
  }));

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Real-time platform statistics" />
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <div className="bg-white border border-border rounded-2xl p-5 shadow-sm" data-testid="live-active">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-teal">
              <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal/60" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-teal" /></span>
              <span className="text-sm font-semibold">Active Now</span>
            </div>
            <Radio className="w-5 h-5 text-teal" />
          </div>
          <p className="font-heading text-4xl font-bold mt-2">{live ? live.active_now : "…"}</p>
          <p className="text-xs text-muted-foreground mt-1">Students active in the last 15 min{refreshed ? ` · updated ${refreshed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : ""}</p>
        </div>
        <div className="bg-white border border-border rounded-2xl p-5 shadow-sm" data-testid="live-new-today">
          <div className="flex items-center justify-between"><span className="text-sm font-semibold text-brandgreen">New Students Today</span><UserPlus className="w-5 h-5 text-brandgreen" /></div>
          <p className="font-heading text-4xl font-bold mt-2">{live ? live.new_today : "…"}</p>
          <p className="text-xs text-muted-foreground mt-1">Yesterday: <b>{live ? live.new_yesterday : "—"}</b> · resets at midnight</p>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {CARDS.map((c) => (
          <button key={c.key} onClick={() => nav(c.to)}
            className="bg-white border border-border rounded-2xl p-5 shadow-sm text-left hover:-translate-y-0.5 hover:shadow-md hover:border-teal/30 transition-all" data-testid={`stat-${c.key}`}>
            <c.icon className={`w-6 h-6 ${c.color}`} />
            <p className="font-heading text-3xl font-bold mt-3">{stats[c.key] ?? 0}</p>
            <p className="text-sm text-muted-foreground">{c.label}</p>
          </button>
        ))}
      </div>

      {!analyticsErr && (
        <div className="mt-8 bg-white border border-border rounded-2xl p-5 sm:p-6 shadow-sm" data-testid="engagement-chart">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-teal" />
              <h3 className="font-heading font-semibold">Student Engagement (last 14 days)</h3>
            </div>
            {analytics && (
              <div className="flex gap-4 text-sm">
                <span className="text-muted-foreground">Courses: <b className="text-teal">{analytics.total_course_hours}h</b></span>
                <span className="text-muted-foreground">Other: <b className="text-brandgreen">{analytics.total_other_hours}h</b></span>
                <span className="text-muted-foreground">Total: <b>{analytics.total_hours}h</b></span>
              </div>
            )}
          </div>
          {chartData.length === 0 ? <Loader label="Loading analytics..." /> : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="#94A3B8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94A3B8" unit="h" />
                <Tooltip formatter={(v) => `${v}h`} />
                <Legend />
                <Bar dataKey="Courses" stackId="a" fill="#1DB87A" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Other" stackId="a" fill="#2ECC71" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
          <p className="text-xs text-muted-foreground mt-2">Hours students spent on the platform per day — split between course content and other pages.</p>
        </div>
      )}
    </div>
  );
}
